<template>
	<Layout
		:show-header="!isMobileViewport"
		:show-footer="!isMobileViewport"
		:show-footer-sponsors="!isMobileViewport"
	>
		<div class="l-container fan-account">
			<div
				v-if="isMobileAccountPage"
				class="fan-account__header"
			>
				<div class="fan-account__profile-icon">
					<Icon
						icon-name="account"
						size="md"
						size-at-md="xl"
					/>
				</div>
				<div
					v-if="profile"
					class="fan-account__profile-details"
				>
					<div class="fan-account__profile-email">
						{{ profile.email }}
					</div>
				</div>
				<button @click="handleMobileClose">
					<Icon
						icon-name="e-remove"
						size="lg"
					/>
				</button>
			</div>
			<div class="l-row">
				<div
					v-if="!isMobileViewport"
					class="l-col l-col--3@md"
				>
					<div
						v-if="profile"
						class="fan-account__profile-details -large"
					>
						<div
							v-if="profile.name"
							class="fan-account__profile-name"
						>
							{{ profile.name }}
						</div>
						<div class="fan-account__profile-email">
							{{ profile.email }}
						</div>
					</div>
					<InPageNavigation
						:nav-items="navItemsMain"
						:dark-theme="!$arenaConfig.isLightTheme"
						:use-vue-router="true"
						:simple-style="true"
					/>
				</div>
				<div class="l-col l-col--9@md">
					<div class="fan-account__content-w">
						<div
							v-if="isMobileViewport && titleForPage"
							class="fan-account__mobile-header"
						>
							<div class="fan-account__mobile-back">
								<button
									class="fan-account__mobile-back-button"
									@click="handleMobileBack"
								>
									<Icon
										icon-name="arrow-left"
										size="lg"
									/>
								</button>
								<span class="fan-account__mobile-back-title">
									{{ titleForPage }}
								</span>
							</div>
							<Button
								v-if="$route.name === 'following'"
								:theme="$arenaConfig.isLightTheme ? null : 'dark'"
								icon="e-add"
								:simplified-on-mobile="true"
								:simplified-on-tablet="true"
								@click="openFollowSearchModal"
							/>
						</div>
						<LoadingSpinner
							v-if="isLoading"
							class="fan-account__loading-spinner"
						/>
						<template v-else>
							<InPageNavigation
								v-if="!isMobileAccountPage && navItemsForPage.length > 0"
								class="fan-account__in-page-navigation"
								:nav-items="navItemsForPage"
								:dark-theme="!$arenaConfig.isLightTheme"
								:horizontal="true"
								:use-vue-router="true"
								:line-column-width="true"
							/>
							<router-view />
						</template>
					</div>
				</div>
			</div>
		</div>
	</Layout>
</template>

<script>
import Layout from '@js/Views/Layout';
import Icon from '@shared/components/Icon';
import InPageNavigation from '@shared/components/InPageNavigation';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import Button from '@shared/components/Button';
import { mapGetters, mapState } from 'vuex';

export default {
	name:       'ViewFanAccount',
	components: { Layout, Icon, InPageNavigation, LoadingSpinner, Button },
	computed:   {
		...mapState({
			profile: state => state.user?.profile,
		}),
		...mapGetters({
			isMobileViewport: 'device/isMobileViewport',
		}),
		isLoading() {
			return !this.profile;
		},
		isMobileAccountPage() {
			return this.$route.name === 'account';
		},
		navItemsMain() {
			const items = [
				{
					label:  this.$I18n.trans('arena.fan_account.my_account'),
					url:    '/account/profile',
					active: ['profile', 'settings', 'security'].includes(this.$route.name),
				},
			];

			if (this.$arenaConfig.paymentsEnabled && !this.profile?.externalProfileUrl) {
				items.push({
					label:  this.$I18n.trans('arena.fan_account.purchases.nav_label'),
					url:    '/account/purchases',
					active: ['purchases'].includes(this.$route.name),
				});
			}

			if (this.$arenaConfig.followingEnabled) {
				items.push({
					label:  this.$I18n.trans('arena.fan_account.following.nav_label'),
					url:    '/account/following',
					active: ['following'].includes(this.$route.name),
				});
			}

			return items;
		},
		navItemsForPage() {
			const items = [];
			// if router name is profile
			if (['profile', 'settings', 'security'].includes(this.$route.name)) {
				items.push({
					label:  this.$I18n.trans('commons.profile'),
					url:    '/account/profile',
					icon:   'pen',
					active: false,
				});

				if (!this.$arenaConfig.profileFields?.isReadOnly) {
					items.push({
						label:  this.$I18n.trans('commons.security'),
						url:    '/account/security',
						icon:   'lock',
						active: false,
					});
				}

				// Settings currently only contains no spoiler mode. If we'll add more, settings needs to be a default nav item.
				// noSpoilerMode setting is then handled again in the Settings.vue component.
				if (this.$arenaConfig.enableNoSpoilerMode) {
					items.push({
						label:  this.$I18n.trans('commons.settings'),
						url:    '/account/settings',
						icon:   'settings-gear',
						active: false,
					});
				}
			}

			return items;
		},
		titleForPage() {
			if (['profile', 'settings', 'security'].includes(this.$route.name)) {
				return this.$I18n.trans('arena.fan_account.my_account');
			}
			if (['purchases'].includes(this.$route.name)) {
				return this.$I18n.trans('arena.fan_account.purchases.nav_label');
			}
			if (['following'].includes(this.$route.name)) {
				return this.$I18n.trans('arena.fan_account.following.nav_label');
			}
			return null;
		},
	},
	created() {
		this.$store.subscribe((mutation) => {
			if (mutation.type === 'user/flushProfile') {
				window.location.href = window.location.origin;
			}
		});

		if (!this.$arenaConfig.loadArenaProfile) {
			window.location.href = window.location.origin;
		}
	},
	methods: {
		handleMobileBack() {
			this.$router.push('/account');
		},
		handleMobileClose() {
			window.location.href = window.location.origin;
		},
		openFollowSearchModal() {
			this.eventBus.$emit('modal:followSearchModal');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.fan-account {
	margin-top: $sp5;
	margin-bottom: $sp6;

	@include size(md-up) {
		margin-top: $sp8;
	}
}

.fan-account__header {
	display: flex;
	gap: $sp4;
	align-items: center;
	margin-bottom: $sp4;
}

.fan-account__profile-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: var(--color-grey-750);
	border: 1px solid var(--color-grey-600);
	color: var(--color-grey-050);
	flex-shrink: 0;

	body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		background-color: var(--color-white);
	}
}

.fan-account__profile-details {
	overflow: hidden;
	flex-grow: 1;

	&.-large {
		margin-bottom: $sp6;
	}
}

.fan-account__profile-name {
	@include ellipsis;
	@include font-size(fs-150);
	margin-bottom: $sp2;
}

.fan-account__profile-email {
	@include ellipsis;

	.fan-account__profile-details.-large & {
		color: var(--color-grey-400);
	}
}

.fan-account__content-w {
	position: relative;
}

.fan-account__mobile-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: $sp6;
	position: relative;

	&::after {
		content: '';
		position: absolute;
		bottom: -$sp4;
		left: 50%;
		transform: translateX(-50%);
		width: 100vw;
		height: 1px;
		background-color: var(--color-grey-900);
	}
}

.fan-account__mobile-back {
	@include font-size(fs-170);
	@include ellipsis;
	display: flex;
	align-items: center;
	gap: $sp4;
}

.fan-account__mobile-back-button {
	display: flex;
	align-items: center;
	justify-content: center;
}

.fan-account__mobile-back-title {
	@include ellipsis;
}

.fan-account__loading-spinner {
	position: absolute;
	right: 50%;
	top: 10vh;
	transform: translateX(-50%);
}

.fan-account__in-page-navigation {
	margin-bottom: $sp6;

	@include size(sm-up) {
		margin-bottom: $sp7;
	}
}
</style>
